import { ColumnsType } from 'antd/es/table';
import { P6p9RecordType } from '@nxsys/types/models/hmrcUpdates/p6p9';
import AppIconButton from '@nxsys/components/AppIconButton';
import { IconCheckCircle, IconCloseCircle } from '@nxsys/components/AppIconSvg';
import moment from 'moment-timezone';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { formatStatus } from '@nxsys/utils';

type P6P9PendingColumnsProps = {
  handleApply: (record: P6p9RecordType) => void;
  handleCancel: (record: P6p9RecordType) => void;
  messages: TypedMessages;
  checkBoxHeader: boolean;
  handleSelectAllRow: (e: any) => void;
  handleSelectRow: (id: number, checked: boolean) => void;
  selectedRowIds: number[];
};

type P6P9AppliedCancelledColumnsProps = {
  messages: TypedMessages;
  isAgency?: boolean;
  handleApply: (record: P6p9RecordType) => void;
  handleCancel: (record: P6p9RecordType) => void;
};

export const getP6P9PendingColumns = ({
  handleApply,
  handleCancel,
  messages,
  checkBoxHeader,
  handleSelectAllRow,
  handleSelectRow,
  selectedRowIds,
}: P6P9PendingColumnsProps): ColumnsType<P6p9RecordType> => [
  {
    key: 'checkbox',
    title: (
      <AppCheckbox
        checked={checkBoxHeader}
        onChange={handleSelectAllRow}
        size="small"
      />
    ),
    fixed: 'left',
    width: 80,
    render: (record: P6p9RecordType) => (
      <AppCheckbox
        checked={selectedRowIds?.includes(record.id)}
        onChange={(e) => handleSelectRow(record.id, e.target.checked)}
        size="small"
      />
    ),
  },
  {
    title: messages['table.actions'],
    fixed: 'left',
    width: 100,
    render: (record: P6p9RecordType) => (
      <ul className="table-actions flex items-center justify-center">
        <li>
          <AppIconButton
            title={messages['common.apply']}
            icon={<IconCheckCircle color="#008000" width={20} height={20} />}
            onClick={handleApply.bind(this, record)}
          />
        </li>
        <li>
          <AppIconButton
            title={messages['common.cancel']}
            icon={<IconCloseCircle />}
            onClick={handleCancel.bind(this, record)}
          />
        </li>
      </ul>
    ),
  },
  {
    title: messages['common.employer'],
    width: 250,
    dataIndex: 'request',
    render: (value) => value?.paymentCompany?.companyName || '_',
  },
  {
    title: messages['hmrcUpdates.hmrcId'],
    dataIndex: 'worksNumber',
    width: 250,
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.nxsysId'],
    width: 250,
    render: (record) => record?.payId || record?.previousPayrollNo || '_',
  },
  {
    title: messages['table.employeeName'],
    width: 250,
    render: (record) => {
      const { forename, surname, subcontractorRef } = record;
      return (
        subcontractorRef?.fullName ||
        `${subcontractorRef?.forename || forename || ''} ${subcontractorRef?.surname || surname || ''}`.trim() ||
        '_'
      );
    },
  },
  {
    title: messages['table.niNumber'],
    width: 250,
    dataIndex: 'NINumber',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.dateIssued'],
    width: 250,
    dataIndex: 'issuedDate',
    render: (value: string) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.dateEffective'],
    width: 250,
    dataIndex: 'effectiveDate',
    render: (value: string) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.taxCodeApplied'],
    width: 250,
    dataIndex: 'taxCode',
    render: () => '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.taxCodeSuggested'],
    width: 250,
    dataIndex: 'taxCode',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.taxBasisApplied'],
    width: 150,
    render: () => '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.taxBasisSuggested'],
    width: 250,
    render: (record) => (record?.Wk1M1 ? 'W1M1' : 'CUMULATIVE'),
  },
  {
    title: messages['hmrcUpdates.p6p9.p45GrossToDateApplied'],
    width: 250,
    render: () => '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.p45GrossToDateSuggested'],
    width: 250,
    dataIndex: 'p45GrossToDate',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.p45TaxDeductedApplied'],
    width: 250,
    render: () => '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.p45TaxDeductedSuggested'],
    width: 250,
    dataIndex: 'p45TaxDeducted',
    render: (value) => value || '_',
  },
  {
    key: 'error',
    title: messages['hmrcUpdates.p6p9.errorMessage'],
    width: 250,
    dataIndex: 'message',
    render: (value) => value || '_',
  },
];

export const getP6P9AppliedCancelledColumns = ({
  messages,
  isAgency,
  handleApply,
  handleCancel,
}: P6P9AppliedCancelledColumnsProps): ColumnsType<P6p9RecordType> => [
  ...(isAgency
    ? [
        {
          title: messages['table.actions'],
          fixed: 'left' as const,
          width: 100,
          render: (record: P6p9RecordType) => (
            <ul className="table-actions flex items-center justify-center">
              <li>
                <AppIconButton
                  title={messages['common.apply']}
                  icon={
                    <IconCheckCircle color="#008000" width={20} height={20} />
                  }
                  onClick={handleApply.bind(this, record)}
                />
              </li>
              <li>
                <AppIconButton
                  title={messages['common.cancel']}
                  icon={<IconCloseCircle />}
                  onClick={handleCancel.bind(this, record)}
                />
              </li>
            </ul>
          ),
        },
      ]
    : []),
  {
    key: 'status',
    title: messages['hmrcUpdates.p6p9.appliedCancelled'],
    width: 170,
    dataIndex: 'status',
    render: (value) => (
      <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
        {formatStatus(value)}
      </AppTagStatus>
    ),
  },
  {
    title: messages['common.employer'],
    dataIndex: 'request',
    width: 350,
    render: (value) => value?.paymentCompany?.companyName || '_',
  },
  {
    title: messages['hmrcUpdates.hmrcId'],
    dataIndex: 'worksNumber',
    width: 250,
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.nxsysId'],
    width: 250,
    render: (record) =>
      record?.payId ||
      record?.previousPayrollNo ||
      (record.subcontractorRef ? record.worksNumber : '_'),
  },
  {
    title: messages['table.employeeName'],
    width: 250,
    render: (record) => {
      const { forename, surname, subcontractorRef } = record;
      return (
        subcontractorRef?.fullName ||
        `${subcontractorRef?.forename || forename || ''} ${subcontractorRef?.surname || surname || ''}`.trim() ||
        '_'
      );
    },
  },
  {
    title: messages['table.niNumber'],
    dataIndex: 'NINumber',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.dateIssued'],
    dataIndex: 'issuedDate',
    render: (value) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
  },
  {
    title: messages['hmrcUpdates.p6p9.dateEffective'],
    dataIndex: 'effectiveDate',
    render: (value) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
  },
  {
    title: messages['hmrcUpdates.p6p9.taxCodeApplied'],
    render: (record) => record?.dataApplied?.taxCode || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.taxCodeSuggested'],
    dataIndex: 'taxCode',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.taxBasisApplied'],
    render: (record) =>
      record?.Wk1M1 ? record?.dataApplied?.taxBasis || 'W1M1' : 'CUMULATIVE',
  },
  {
    title: messages['hmrcUpdates.p6p9.taxBasisSuggested'],
    render: (record) => (record?.Wk1M1 ? 'W1M1' : 'CUMULATIVE'),
  },
  {
    title: messages['hmrcUpdates.p6p9.p45GrossToDateApplied'],
    render: (record) => record?.dataApplied?.p45GrossToDate || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.p45GrossToDateSuggested'],
    dataIndex: 'p45GrossToDate',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.p45TaxDeductedApplied'],
    render: (record) => record?.dataApplied?.p45TaxToDate || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.p45TaxDeductedSuggested'],
    dataIndex: 'p45TaxDeducted',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.appliedCancelled'],
    dataIndex: 'status',
    render: (value) => (value ? value[0] : '_'),
  },
  {
    title: messages['hmrcUpdates.p6p9.dateAppliedCancelled'],
    dataIndex: 'dateAppliedOrCancelled',
    render: (value) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
  },
];
